import React, { useState, useEffect, useCallback } from "react";
import PMConnectors from "./PMConnectors";
import PMScheduleCalendar from "./PMScheduleCalendar";
import PMAnalytics from "./PMAnalytics";
import UpgradeToCMOBanner from "./UpgradeToCMOBanner";
import SchedulePostModal from "./SchedulePostModal";
import { postizAPI } from "../api/postizAPI";

/**
 * Performance Marketing Module
 * Main wrapper component for Performance Marketing feature
 * - Shows upsell for non-CMO SERVICE users
 * - Shows PM dashboard for CMO SERVICE users
 * - Handles Postiz user sync and token management
 */
export default function PerformanceMarketing({ isDarkMode, user, currentPlan, activeRole }) {
  const [postizToken, setPostizToken] = useState(null);
  const [, setPostizUserId] = useState(null);
  const [activeTab, setActiveTab] = useState("analytics"); // connectors, calendar, analytics
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCMOService, setIsCMOService] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [editPostData, setEditPostData] = useState(null); // { caption, integrationId, scheduledDate }

  // Check user tier and sync to Postiz if needed
  useEffect(() => {
    const initializePerformanceMarketing = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if user is on a CMO plan.
        // Prefer the stable API role (e.g. "cmo_service") so it keeps working
        // when the plan display name changes to "CMO Service v2", v3, etc.
        const roleStr = String(activeRole).toLowerCase();
        const planFromUser =
          user?.currentPlan ||
          user?.display_name ||
          user?.customer?.currentPlan ||
          user?.customer?.display_name ||
          user?.planKey ||
          user?.planName ||
          "";
        const planToCheck = currentPlan || planFromUser;
        const planStr = String(planToCheck).toLowerCase();
        const isCMO = roleStr === "cmo_service" || planStr.includes("cmo");

        setIsCMOService(isCMO);

        if (!isCMO) {
          setLoading(false);
          return;
        }

        // Try to get existing Postiz token from session/storage
        const storedToken = sessionStorage.getItem("postiz_token");
        if (storedToken) {
          setPostizToken(storedToken);
          setPostizUserId(sessionStorage.getItem("postiz_user_id"));
          setLoading(false);
          return;
        }

        // No token in storage - check Postiz first, then sync only when needed
        if (!user?.email || !user?.name) {
          setError("User information incomplete for Performance Marketing sync");
          setLoading(false);
          return;
        }

        // Ensure a distinct, non-empty fallback organization name is provided
        const externalId = user.id || user.userId;
        const finalOrgName = "Demo_Organization"; // ← placeholder, Postiz backend will handle fallback logic

        const syncPayload = {
          email: user.email,
          name: user.name,
          organizationName: finalOrgName, // Guarantees a value like "Uday pawar Org"
          externalUserId: externalId,
          datafast_visitor_id: user.datafast_visitor_id,
        };

        const statusResult = await postizAPI.checkStatus(externalId, user.email);

        if (statusResult?.success === false && statusResult?.error) {
          console.warn('Infinium status check failed, falling back to sync:', statusResult.error);
        }

        if (statusResult?.userExists && statusResult?.orgExists && statusResult?.token) {
          sessionStorage.setItem("postiz_token", statusResult.token);
          sessionStorage.setItem("postiz_user_id", statusResult.postizUserId);
          sessionStorage.setItem("postiz_org_id", statusResult.organizationId);

          setPostizToken(statusResult.token);
          setPostizUserId(statusResult.postizUserId);
          setLoading(false);
          return;
        }

        // Debug log to confirm exactly what payload is heading out
        console.log('Syncing user with data:', syncPayload);

        const syncResult = await postizAPI.syncUser(syncPayload);

        if (syncResult.success) {
          // Store token in session storage
          sessionStorage.setItem("postiz_token", syncResult.data.token);
          sessionStorage.setItem("postiz_user_id", syncResult.data.postizUserId);
          sessionStorage.setItem("postiz_org_id", syncResult.data.organizationId);

          setPostizToken(syncResult.data.token);
          setPostizUserId(syncResult.data.postizUserId);
        } else {
          setError(`Failed to initialize Performance Marketing: ${syncResult.error}`);
        }
      } catch (err) {
        console.error("Error initializing Performance Marketing:", err);
        setError(err.message || "Unknown error initializing Performance Marketing");
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      initializePerformanceMarketing();
    }
  }, [user, currentPlan, activeRole]);

  // Stable callback — must be before any early returns to satisfy Rules of Hooks
  const handleConnectorsLoaded = useCallback((list) => {
    setConnectors(list);
    sessionStorage.setItem('postiz_connectors', JSON.stringify(list));
  }, []);

  // Eagerly load connectors once we have a token so the edit modal
  // has platform data available before the user visits the Connectors tab.
  useEffect(() => {
    if (!postizToken) return;
    postizAPI.getConnectors(postizToken).then((result) => {
      if (result.success && result.data?.length) {
        setConnectors(result.data);
        sessionStorage.setItem('postiz_connectors', JSON.stringify(result.data));
      }
    });
  }, [postizToken]);

  // Show upsell if not CMO SERVICE
  if (!isCMOService && !loading) {
    return <UpgradeToCMOBanner isDarkMode={isDarkMode} />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-white"}`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mb-4 ${isDarkMode ? "border-cyan-400" : "border-cyan-500"}`}></div>
          <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>Initializing Performance Marketing...</p>
        </div>
      </div>
    );
  }

  // Show error if sync failed
  if (error) {
    return (
      <div className={`flex-1 flex flex-col items-center justify-center p-6 ${isDarkMode ? "bg-gray-900" : "bg-white"}`}>
        <div className={`rounded-lg p-6 max-w-md ${isDarkMode ? "bg-red-900/20 border border-red-700" : "bg-red-50 border border-red-200"}`}>
          <h3 className={isDarkMode ? "text-red-400" : "text-red-600"}>Error</h3>
          <p className={`mt-2 ${isDarkMode ? "text-red-300" : "text-red-700"}`}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /**
   * Called by PMScheduleCalendar when the edit button is clicked.
   * Fetches the full post group (which includes image data) then opens the modal.
   */
  const handleEditPost = async (post) => {
    // Parse the scheduled date into date + time parts for the modal
    let initialDate = "";
    let initialTime = "09:00";
    if (post.scheduledDate || post.createdAt) {
      const d = new Date(post.scheduledDate || post.createdAt);
      if (!isNaN(d.getTime())) {
        initialDate = d.toISOString().split("T")[0];
        initialTime = d.toTimeString().slice(0, 5); // "HH:MM"
      }
    }

    // Find the connector IDs that match this post's platforms
    const matchedIds = connectors
      .filter((c) => {
        const pid = (c.platform || "").toLowerCase();
        return post.platforms?.some((p) => (p || "").toLowerCase() === pid);
      })
      .map((c) => c.id);

    // Fetch the full post group to get the image URL (not available from list endpoint)
    let imageUrl = post.imageUrl || null;
    const groupId = post.group || post.id;
    if (groupId && postizToken) {
      const groupResult = await postizAPI.getPostGroup(postizToken, groupId);
      if (groupResult.success && groupResult.data?.imageUrl) {
        imageUrl = groupResult.data.imageUrl;
      }
    }

    setEditPostData({
      postId: post.id,
      group: post.group || post.id,
      caption: post.caption || post.body || "",
      mediaUrl: imageUrl,
      initialSelectedIds: matchedIds,
      integrationId: post.platforms?.[0] || "",
      initialDate,
      initialTime,
    });
    setShowCreatePost(true);
  };

    // Show PM dashboard (only if token exists)
  if (!postizToken) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-white"}`}>
        <div className={`rounded-lg p-6 max-w-md text-center ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}>
          <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>Performance Marketing is initializing. Please wait...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col overflow-hidden ${isDarkMode ? "bg-gray-900" : "bg-white"}`}>
      {/* Header with Tabs */}
      <div className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"}`}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Performance Marketing
            </h1>
            <div className="flex items-center gap-3">
              {connectors.some((c) => !c.disabled && !c.refreshNeeded && !c.inBetweenSteps) && (
                <button
                  onClick={() => setShowCreatePost(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition"
                >
                  + Create Post
                </button>
              )}
              <div className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                Connected to Postiz • <span className={`font-medium ${isDarkMode ? "text-cyan-400" : "text-cyan-600"}`}>Active</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-4 border-t border-gray-600/20 pt-4">
            <button
              onClick={() => setActiveTab("analytics")}
              className={`px-4 py-2 rounded-t font-medium transition-colors ${
                activeTab === "analytics"
                  ? isDarkMode
                    ? "bg-cyan-500/20 text-cyan-400 border-b-2 border-cyan-400"
                    : "bg-cyan-50 text-cyan-600 border-b-2 border-cyan-600"
                  : isDarkMode
                  ? "text-gray-400 hover:text-gray-300"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              📊 Analytics
            </button>
            <button
              onClick={() => setActiveTab("calendar")}
              className={`px-4 py-2 rounded-t font-medium transition-colors ${
                activeTab === "calendar"
                  ? isDarkMode
                    ? "bg-cyan-500/20 text-cyan-400 border-b-2 border-cyan-400"
                    : "bg-cyan-50 text-cyan-600 border-b-2 border-cyan-600"
                  : isDarkMode
                  ? "text-gray-400 hover:text-gray-300"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              📅 Schedule Calendar
            </button>
            <button
              onClick={() => setActiveTab("connectors")}
              className={`px-4 py-2 rounded-t font-medium transition-colors ${
                activeTab === "connectors"
                  ? isDarkMode
                    ? "bg-cyan-500/20 text-cyan-400 border-b-2 border-cyan-400"
                    : "bg-cyan-50 text-cyan-600 border-b-2 border-cyan-600"
                  : isDarkMode
                  ? "text-gray-400 hover:text-gray-300"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              📱 Social Connectors
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "connectors" && (
          <PMConnectors
            isDarkMode={isDarkMode}
            token={postizToken}
            onConnectorsLoaded={handleConnectorsLoaded}
          />
        )}
        {activeTab === "calendar" && (
          <PMScheduleCalendar isDarkMode={isDarkMode} token={postizToken} onEditPost={handleEditPost} />
        )}
        {activeTab === "analytics" && (
          <PMAnalytics isDarkMode={isDarkMode} token={postizToken} />
        )}
      </div>

      {/* Create Post Modal */}
      <SchedulePostModal
        isOpen={showCreatePost}
        onClose={() => { setShowCreatePost(false); setEditPostData(null); }}
        isDarkMode={isDarkMode}
        token={postizToken}
        connectors={connectors.filter((c) => !c.disabled && !c.refreshNeeded && !c.inBetweenSteps)}
        postId={editPostData?.postId || null}
        postGroup={editPostData?.group || null}
        mediaUrl={editPostData?.mediaUrl || null}
        initialCaption={editPostData?.caption || ""}
        initialSelectedIds={editPostData?.initialSelectedIds || []}
        initialDate={editPostData?.initialDate || ""}
        initialTime={editPostData?.initialTime || "09:00"}
        onScheduleSuccess={() => { setShowCreatePost(false); setEditPostData(null); }}
      />
    </div>
  );
}